import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardMedia,
    CardContent,
    Button,
    Skeleton,
    Chip,
    IconButton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../store';
import { setMenuScrollState } from '../store/slices/uiSlice';
import {
    setCurrentParentItem,
    addRecommendedCustomization,
    updateRecommendedCustomization,
    setRecommendedItemUpdating
} from '../store/slices/recommendedItemsSlice';
import type { RecommendedItemCustomization } from '../store/slices/recommendedItemsSlice';
import api from '../services/api';
import type { MenuItem } from '../types/menu';

interface RecommendedItemsProps {
    currentItemId: string;
    currentItemName: string;
    onItemAdded?: () => void;
    isEditing?: boolean;
    selectedCategory?: number;
}

interface RecommendedItem extends MenuItem {
    discountPercentage?: number;
    bundlePrice?: number;
}

const RecommendedItems: React.FC<RecommendedItemsProps> = ({ 
    currentItemId, 
    isEditing = false,
    selectedCategory = 0
}) => {
    const [recommendedItems, setRecommendedItems] = useState<RecommendedItem[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const recommendedItemsState = useSelector((state: RootState) => state.recommendedItems);
    
    // Get customizations for current parent item
    const parentCustomizations = recommendedItemsState.parentRecommendations[currentItemId] || {};

    useEffect(() => {
        // Set current parent item in state
        dispatch(setCurrentParentItem(currentItemId));
        fetchRecommendedItems();
    }, [currentItemId, dispatch]);

    const fetchRecommendedItems = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/menu/items/${currentItemId}/recommended`);
            setRecommendedItems(response.data);
        } catch (error) {
            console.error('Failed to fetch recommended items:', error);
            // For now, show empty state since endpoint doesn't exist yet
            setRecommendedItems([]);
        } finally {
            setLoading(false);
        }
    };

    const handleQuantityChange = (item: RecommendedItem, delta: number) => {
        const existingCustomization = parentCustomizations[item.id];
        const currentQuantity = existingCustomization?.quantity || 0;
        const newQuantity = Math.max(0, currentQuantity + delta);

        if (newQuantity === 0) {
            // Remove customization if quantity becomes 0
            if (existingCustomization) {
                dispatch(updateRecommendedCustomization({
                    parentItemId: currentItemId,
                    menuItemId: item.id,
                    updates: { quantity: 0, addedToCart: false }
                }));
            }
            return;
        }

        // Create or update customization (but don't add to cart yet)
        const customization: RecommendedItemCustomization = {
            id: existingCustomization?.id || `${item.id}-${Date.now()}`,
            menuItemId: item.id,
            name: item.name,
            price: item.bundlePrice || item.price,
            quantity: newQuantity,
            selectedOptions: existingCustomization?.selectedOptions || {},
            specialInstructions: existingCustomization?.specialInstructions || '',
            isUpdating: false,
            addedToCart: existingCustomization?.addedToCart || false // Keep existing cart status
        };

        if (existingCustomization) {
            dispatch(updateRecommendedCustomization({
                parentItemId: currentItemId,
                menuItemId: item.id,
                updates: customization
            }));
        } else {
            dispatch(addRecommendedCustomization({
                parentItemId: currentItemId,
                customization
            }));
        }
    };

    const handleCustomize = (item: RecommendedItem) => {
        // Save current scroll position and category before navigating
        dispatch(setMenuScrollState({
            scrollY: window.scrollY,
            selectedCategory,
            timestamp: Date.now()
        }));

        const existingCustomization = parentCustomizations[item.id];
        
        // Mark as updating if there's an existing customization
        if (existingCustomization) {
            dispatch(setRecommendedItemUpdating({
                parentItemId: currentItemId,
                menuItemId: item.id,
                isUpdating: true
            }));
        }
        
        navigate(`/customize-recommended/${item.id}`, {
            state: { 
                parentItemId: currentItemId,
                isUpdating: !!existingCustomization,
                existingCustomization
            }
        });
    };

    if (loading) {
        return (
            <Box sx={{ py: 3, px: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    También te puede interesar
                </Typography>
                <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    overflowX: 'auto',
                    pb: 1,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none'
                }}>
                    {[1, 2, 3].map((index) => (
                        <Box key={index} sx={{ minWidth: 280, flexShrink: 0 }}>
                            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 1 }} />
                            <Skeleton variant="text" height={24} sx={{ mb: 0.5 }} />
                            <Skeleton variant="text" height={20} width="60%" />
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    // Don't show recommendations when editing an existing item
    if (isEditing || recommendedItems.length === 0) {
        return null;
    }

    return (
        <Box sx={{ py: 3, px: 2, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocalOfferIcon sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                    Productos recomendados:
                </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Clientes que pidieron este producto también compraron:
            </Typography>

            <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                overflowX: 'auto',
                pb: 2,
                '&::-webkit-scrollbar': { 
                    height: 4,
                },
                '&::-webkit-scrollbar-track': {
                    bgcolor: 'grey.200',
                    borderRadius: 2,
                },
                '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'primary.main',
                    borderRadius: 2,
                },
                scrollbarWidth: 'thin',
                scrollbarColor: `${theme.palette.primary.main} ${theme.palette.grey[200]}`
            }}>
                {recommendedItems.map((item) => (
                    <Card 
                        key={item.id} 
                        sx={{ 
                            width: isMobile ? 240 : 280,
                            flexShrink: 0,
                            borderRadius: 3,
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                            }
                        }}
                    >
                        <Box sx={{ position: 'relative' }}>
                            <CardMedia
                                component="img"
                                height="120"
                                image={item.imageUrl || '/placeholder-food.jpg'}
                                alt={item.name}
                                sx={{ 
                                    objectFit: 'cover',
                                    bgcolor: 'grey.100'
                                }}
                            />
                            {item.discountPercentage && (
                                <Chip
                                    label={`-${item.discountPercentage}%`}
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        bgcolor: 'error.main',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem'
                                    }}
                                />
                            )}
                        </Box>
                        
                        <CardContent sx={{ p: 2 }}>
                            <Typography 
                                variant="subtitle2" 
                                fontWeight="bold" 
                                sx={{ 
                                    mb: 0,
                                    lineHeight: 1.2,
                                    height: '1.5em',
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                }}
                            >
                                {item.name}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                {item.bundlePrice ? (
                                    <>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ textDecoration: 'line-through', mr: 1 }}
                                        >
                                            ₡{Number(item.price).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </Typography>
                                        <Typography variant="h6" fontWeight="bold" color="primary.main">
                                            ₡{Number(item.bundlePrice).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                                        ₡{Number(item.price).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Button
                                    variant={parentCustomizations[item.id]?.addedToCart ? "contained" : "outlined"}
                                    size="small"
                                    onClick={() => handleCustomize(item)}
                                    sx={{ 
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        py: 0.75,
                                        flexGrow: 1,
                                        minWidth: 0,
                                        mr: 1,
                                        bgcolor: parentCustomizations[item.id]?.addedToCart ? 'primary.main' : 'transparent',
                                        color: parentCustomizations[item.id]?.addedToCart ? 'white' : 'primary.main',
                                        '&:hover': {
                                            bgcolor: parentCustomizations[item.id]?.addedToCart ? 'primary.dark' : 'primary.50'
                                        }
                                    }}
                                >
                                    {parentCustomizations[item.id]?.addedToCart ? 'Actualizar' : 'Personalizar'}
                                </Button>
                                
                                {/* Quantity Controls */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    gap: 1,
                                    bgcolor: 'grey.50',
                                    borderRadius: 2,
                                    p: 0.5
                                }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleQuantityChange(item, -1)}
                                        disabled={(parentCustomizations[item.id]?.quantity || 0) === 0}
                                        sx={{ 
                                            width: 28,
                                            height: 28,
                                            color: 'primary.main',
                                            '&.Mui-disabled': {
                                                color: 'grey.400'
                                            }
                                        }}
                                    >
                                        {(parentCustomizations[item.id]?.quantity || 0) <= 1 ? (
                                            <DeleteIcon fontSize="small" />
                                        ) : (
                                            <RemoveIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                    
                                    <Typography 
                                        variant="body2" 
                                        fontWeight="bold"
                                        sx={{ 
                                            minWidth: '20px', 
                                            textAlign: 'center',
                                            color: 'text.primary'
                                        }}
                                    >
                                        {parentCustomizations[item.id]?.quantity || 0}
                                    </Typography>
                                    
                                    <IconButton
                                        size="small"
                                        onClick={() => handleQuantityChange(item, 1)}
                                        sx={{ 
                                            width: 28,
                                            height: 28,
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            '&:hover': {
                                                bgcolor: 'primary.dark'
                                            },
                                            '&.Mui-disabled': {
                                                bgcolor: 'grey.300',
                                                color: 'grey.500'
                                            }
                                        }}
                                    >
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </Box>
        </Box>
    );
};

export default RecommendedItems;